import { onError } from "@apollo/client/link/error";

/**
 * Module-level mutable callback ref.
 *
 * GlobalErrorHandler updates this at runtime so the Apollo error link
 * (created once during App mount) can delegate to the current toast logic
 * without needing React context in the link chain.
 *
 * @type {{ current: Function | null }}
 */
export const errorToastCallback = { current: null };

/**
 * Formats a GraphQL error into a human-readable toast message.
 *
 * @param {Object} params
 * @param {string} params.message - The error message
 * @param {string} params.operationName - The GraphQL operation name
 * @returns {string} Formatted message
 */
export function formatGraphQLError({ message, operationName }) {
  const opLabel = operationName ? ` [${operationName}]` : "";
  return `GraphQL${opLabel}: ${message}`;
}

/**
 * Apollo error handler that reports GraphQL and network errors
 * through the errorToastCallback ref.
 *
 * Exported separately for testability.
 *
 * @param {Object} params
 * @param {Array} [params.graphQLErrors] - Array of GraphQL errors
 * @param {Object} [params.networkError] - Network error object
 * @param {Object} [params.operation] - The GraphQL operation
 */
export function handleApolloError({ graphQLErrors, networkError, operation }) {
  const operationName = operation?.operationName || "";

  if (graphQLErrors) {
    graphQLErrors.forEach((gqlError) => {
      const message = formatGraphQLError({
        message: gqlError.message,
        operationName,
      });

      console.error("[ApolloErrorLink] GraphQL error:", message, gqlError);

      if (typeof errorToastCallback.current === "function") {
        errorToastCallback.current(message);
      }
    });
  }

  if (networkError) {
    const status = networkError.statusCode
      ? ` (${networkError.statusCode})`
      : "";
    const message = `GraphQL Network Error${status}: ${
      networkError.message || "Unable to reach server"
    }`;

    console.error("[ApolloErrorLink] Network error:", message, networkError);

    if (typeof errorToastCallback.current === "function") {
      errorToastCallback.current(message);
    }
  }
}

/**
 * Creates an Apollo error link that reports GraphQL and network errors
 * through the errorToastCallback ref.
 *
 * @returns {import("@apollo/client").ApolloLink}
 */
export function createErrorLink() {
  return onError(handleApolloError);
}
